// src/pages/MyRankings_Decks.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "../App.css";
import GetSnapDeck from "./sql/GetSnapDeck.jsx";

function MyRankings_Decks({ user }) {
  const [decks, setDecks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [topCards, setTopCards] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchDecks();
  }, [user]);

  const fetchDecks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/snapDecks/user/${user.id}`
      );
      setDecks(res.data);
      setLoading(false);

      // Preload top 3 cards for each deck
      res.data.forEach((deck) => fetchTopCards(deck.id));
    } catch (err) {
      console.error("Failed to fetch decks:", err);
      setLoading(false);
    }
  };

  const fetchTopCards = async (deckId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}api/data/deckTopCards/${deckId}/${user.id}`
      );
      setTopCards((prev) => ({ ...prev, [deckId]: res.data }));
    } catch (err) {
      console.error(`Failed to fetch top cards for deck ${deckId}:`, err);
    }
  };

  const filteredDecks = useMemo(() => {
    if (!searchTerm.trim()) return decks;
    return decks.filter((d) =>
      d.deckname.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [decks, searchTerm]);

  const BUCKET_ORDER = [
    "Top Tier",
    "Great",
    "Solid",
    "Good",
    "Average",
    "Mediocre",
    "Not Good",
    "Bad",
    "Terrible",
    "Unranked",
  ];

  const groupedData = useMemo(() => {
    const grouped = {};
    filteredDecks.forEach((deck) => {
      const avg = parseFloat(deck.deckranking);
      let bucket = "Unranked";
      if (!isNaN(avg)) {
        if (avg >= 90) bucket = "Top Tier";
        else if (avg >= 85) bucket = "Great";
        else if (avg >= 80) bucket = "Solid";
        else if (avg >= 75) bucket = "Good";
        else if (avg >= 70) bucket = "Average";
        else if (avg >= 65) bucket = "Mediocre";
        else if (avg >= 60) bucket = "Not Good";
        else if (avg >= 55) bucket = "Bad";
        else bucket = "Terrible";
      }
      if (!grouped[bucket]) grouped[bucket] = [];
      grouped[bucket].push(deck);
    });
    return grouped;
  }, [filteredDecks]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "40px" }}>
        <h3>Loading Deck Rankings...</h3>
      </div>
    );
  }

  return (
    <div className="deck-rankings-container">
      <h2 style={{ textAlign: "center" }}>My Deck Rankings</h2>

      {/* Search bar */}
      <div style={{ textAlign: "center", margin: "20px" }}>
        <input
          type="text"
          placeholder="Search decks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px",
            borderRadius: "6px",
            width: "250px",
            textAlign: "center",
            backgroundColor: "#111",
            color: "#f5f5f5",
            border: "1px solid #555",
          }}
        />
      </div>

      {/* Grouped Decks */}
      {BUCKET_ORDER.map(
        (tier) =>
          groupedData[tier] &&
          groupedData[tier].length > 0 && (
            <div key={tier} style={{ marginBottom: "40px" }}>
              <h3
                style={{
                  textAlign: "center",
                  color: "rgb(197,176,55)",
                  marginBottom: "10px",
                }}
              >
                {tier}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: "15px",
                  padding: "10px 20px",
                }}
              >
                {groupedData[tier]
                  .sort((a, b) => (b.deckranking || 0) - (a.deckranking || 0))
                  .map((deck) => (
                    <div
                      key={deck.id}
                      className="deck-card"
                      style={{
                        background: "#1a1a1a",
                        border: "1px solid #333",
                        borderRadius: "12px",
                        padding: "12px",
                        color: "#ddd",
                        textAlign: "center",
                        boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                      }}
                    >
                      <h4 style={{ color: "#f5f5f5", marginBottom: "8px" }}>
                        {deck.deckname}
                      </h4>
                      <p style={{ fontSize: "14px", margin: "4px 0" }}>
                        <strong>Rank:</strong> {deck.deckrank || "N/A"}
                      </p>
                      <p style={{ fontSize: "14px", margin: "4px 0" }}>
                        <strong>Score:</strong>{" "}
                        {deck.deckranking ? deck.deckranking.toFixed(1) : "—"}
                      </p>
                      <p style={{ fontSize: "14px", margin: "4px 0" }}>
                        <strong>Month:</strong>{" "}
                        {deck.monthcompleted || "Unknown"}
                      </p>

                      {/* Top 3 Cards Preview */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        {(topCards[deck.id] || []).map((card, i) => (
                          <div
                            key={i}
                            style={{
                              width: "70px",
                              height: "100px",
                              backgroundColor: "#222",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                            title={card.cardName}
                            dangerouslySetInnerHTML={{ __html: card.cardhtml }}
                          ></div>
                        ))}
                      </div>

                      <button
                        className="button"
                        onClick={() => setSelectedDeck(deck)}
                        style={{
                          marginTop: "10px",
                          background: "rgb(197,176,55)",
                          color: "#000",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )
      )}

      {/* Deck Detail Modal */}
      {selectedDeck && (
        <GetSnapDeck
          deckId={selectedDeck.id}
          onClose={() => setSelectedDeck(null)}
        />
      )}
    </div>
  );
}

export default MyRankings_Decks;
